import { cn } from '@/lib/cn';
import useSize from '@react-hook/size';
import { localPoint } from '@visx/event';
import { LinearGradient } from '@visx/gradient';
import { Grid } from '@visx/grid';
import { bisector } from '@visx/vendor/d3-array';
import NoDataPlaceholder from 'components/NoDataPlaceholder';
import { DataStreamVisualisation } from 'dashboard-engine/types/Visualisation';
import { tickCount } from 'dashboard-engine/util/tickFormatter';
import { LegendWrapper } from 'dashboard-engine/visualisations/components/LegendWrapper';
import { NUM_Y_TICKS } from 'dashboard-engine/visualisations/getGridYValues';
import { kebabCase } from 'lodash';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DataStreamLineGraphConfig } from '../Config';
import { LineGraphLinesLayer } from './LineGraphLinesLayer';
import { LineTooltip } from './LineTooltip';
import { calculateMargins } from './margins';
import { DISABLE_ANIMATION_THRESHOLD, LineGraphSlice, useLines } from './useLines';
import { XAxis } from './XAxis';
import { YAxis } from './YAxis';

const bisectDate = bisector<LineGraphSlice, number>((d) => d.x0).left;

const LineGraph: DataStreamVisualisation<DataStreamLineGraphConfig> = ({
    tileId,
    data,
    config,
    onClick
}) => {
    const margin = useMemo(
        () =>
            calculateMargins(
                config.showLegend ? config.legendPosition : undefined,
                Boolean(config.xAxisLabel && (config.showXAxisLabel ?? true)),
                config.showYAxisLabel ?? true
            ),
        [config]
    );
    const [hoveredLineId, setHoveredLineId] = useState<string | undefined>(undefined);
    const [currentSlice, setCurrentSlice] = useState<LineGraphSlice>();

    const graphRef = useRef<HTMLDivElement>(null);
    const graphWithLegendRef = useRef<HTMLDivElement>(null);

    // If a single ref is used for both scenarios, the width and height end up being 0 when toggling between the two
    // This forces the hook to recalculate the size when the legend is shown or hidden
    const sizeRef = config.showLegend ? graphWithLegendRef : graphRef;
    const [width, height] = useSize(sizeRef);

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const xAxisLabel = config?.xAxisLabel;

    const isLegendVertical = config.legendPosition === 'top' || config.legendPosition === 'bottom';
    const showGrid = config.showGrid ?? true;
    const showLineGraph = width > 0 && height > 0;
    const showXAxisLabel = config.showXAxisLabel ?? true;
    const showYAxisLabel = config.showYAxisLabel ?? true;

    const {
        lines,
        trendLines,
        points,
        slices,
        dateScale,
        valueScale,
        lineGenerator,
        areaGenerator,
        unitLabel,
        shape,
        formatTick
    } = useLines(data, config, width, height, margin);

    // Animation struggles with larger datasets, so disable it for performance
    const disableAnimation = data.rows.length > DISABLE_ANIMATION_THRESHOLD;

    const legend = lines.map((s) => ({
        id: s.id,
        label: s.id,
        color: s.color,
        isFocused: hoveredLineId !== undefined ? hoveredLineId === s.id : true
    }));

    const yAxisLabel = (config?.yAxisLabel ? config.yAxisLabel : null) ?? unitLabel ?? shape?.displayName ?? 'Value';

    const hideXAxisLine = !config?.yAxisRangeMode || config?.yAxisRangeMode === 'auto';

    const handleLineHover = useCallback(
        (lineId: string) => {
            if (lines.length > 1) {
                setHoveredLineId(lineId);
            }
        },
        [setHoveredLineId, lines.length]
    );

    const handleLineLeave = useCallback(() => {
        setHoveredLineId(undefined);
    }, [setHoveredLineId]);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            const { x } = localPoint(e) || { x: 0 };
            const index = bisectDate(slices, x);
            const slice = slices[index - 1];
            if (slice) {
                setCurrentSlice(slice);
            }
        },
        [slices, setCurrentSlice]
    );

    if (!data || data.rows.length === 0) {
        return <NoDataPlaceholder />;
    }

    const areaGradientPrefix = `${tileId}-area-gradient-`;

    const lineGraph = (
        <div
            className={cn('relative w-full h-full font-inter', onClick && 'cursor-pointer')}
            data-visualization='data-stream-line-graph'
            onClick={onClick}
            onMouseLeave={() => {
                setHoveredLineId(undefined);
                setCurrentSlice(undefined);
            }}
        >
            <svg width={width} height={height}>
                {lines.map((line) => (
                    <LinearGradient key={line.id} id={`${areaGradientPrefix}${kebabCase(line.id)}`}>
                        <stop offset='0%' stopColor={line.color} stopOpacity={1} />
                        <stop offset='25%' stopColor={line.color} stopOpacity={1} />
                        <stop offset='100%' stopColor={line.color} stopOpacity={0} />
                    </LinearGradient>
                ))}
                <Grid
                    numTicksRows={showGrid ? NUM_Y_TICKS : 0}
                    numTicksColumns={showGrid ? tickCount(innerWidth) : 0}
                    top={margin.top}
                    left={margin.left}
                    xOffset={-margin.left}
                    yOffset={-margin.top}
                    xScale={dateScale}
                    yScale={valueScale}
                    width={innerWidth}
                    height={innerHeight}
                    stroke='var(--dividerTertiary)'
                />
                <XAxis
                    scale={dateScale}
                    innerWidth={innerWidth}
                    innerHeight={innerHeight}
                    label={showXAxisLabel ? xAxisLabel : undefined}
                    hideAxisLine={hideXAxisLine}
                    marginTop={margin.top}
                />
                <LineGraphLinesLayer
                    lines={lines}
                    points={points}
                    innerWidth={innerWidth}
                    innerHeight={innerHeight}
                    margin={margin}
                    trendLines={trendLines}
                    areaGradientPrefix={areaGradientPrefix}
                    enableArea={config.shading ?? true}
                    enablePoints={config.dataPoints ?? false}
                    disableAnimation={disableAnimation}
                    hoveredLineId={hoveredLineId}
                    lineGenerator={lineGenerator}
                    areaGenerator={areaGenerator}
                    onLineHover={handleLineHover}
                    onLineLeave={handleLineLeave}
                    onMouseMove={handleMouseMove}
                />

                <YAxis scale={valueScale} formatTick={formatTick} label={showYAxisLabel ? yAxisLabel : undefined} />

                {currentSlice && (
                    <LineTooltip
                        currentSlice={currentSlice}
                        hoveredLineId={hoveredLineId}
                        graphRef={sizeRef}
                        innerHeight={innerHeight}
                        marginTop={margin.top}
                    />
                )}
            </svg>
        </div>
    );

    if (config.showLegend && config.legendPosition) {
        return (
            <LegendWrapper
                legend={legend}
                position={config.legendPosition}
                vizRef={graphWithLegendRef}
                containerSizePercentage={isLegendVertical ? 0.3 : 0.25}
                onHover={(attributes) => setHoveredLineId(attributes.id)}
                onLeave={() => setHoveredLineId(undefined)}
                vizContainerClassname='flex-1'
                legendContainerClassname={cn('p-5', { 'pt-0': config.legendPosition === 'top' })}
            >
                <div ref={graphWithLegendRef} className='relative w-full h-full overflow-hidden'>
                    {showLineGraph ? lineGraph : null}
                </div>
            </LegendWrapper>
        );
    }

    return (
        <div ref={graphRef} className='relative w-full h-full overflow-hidden'>
            {showLineGraph ? lineGraph : null}
        </div>
    );
};

export default LineGraph;
